/**
 * SpectatorList: M2.T19.
 *
 * Left-panel component showing connected players with a creator badge.
 *
 * Per IMPLEMENTATION_PLAN §7 M2.T17 + PRD §3.5.
 *
 * Props:
 *   players: serialized player array from ROOM_STATE_SYNC
 *   creatorId: playerId of the room creator (null for game rooms at M3+)
 *   myId: current client's playerId (for "you" annotation)
 */

import type { SerializedPlayer } from "@drawlol/shared";

export type SpectatorListProps = {
  players: ReadonlyArray<SerializedPlayer>;
  creatorId: string | null;
  myId: string | null;
};

export function SpectatorList({ players, creatorId, myId }: SpectatorListProps) {
  if (players.length === 0) {
    return (
      <aside class="spectator-list spectator-list--empty" aria-label="Players">
        <p class="spectator-list__empty-hint">No players yet</p>
      </aside>
    );
  }

  return (
    <aside class="spectator-list" aria-label="Players">
      <ul class="spectator-list__items" role="list">
        {players.map((player) => (
          <SpectatorRow
            key={player.id}
            player={player}
            isCreator={player.id === creatorId}
            isMe={player.id === myId}
          />
        ))}
      </ul>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// SpectatorRow
// ---------------------------------------------------------------------------

type RowProps = {
  player: SerializedPlayer;
  isCreator: boolean;
  isMe: boolean;
};

function SpectatorRow({ player, isCreator, isMe }: RowProps) {
  return (
    <li
      class="spectator-row"
      data-player-id={player.id}
      data-creator={isCreator ? "true" : undefined}
      data-me={isMe ? "true" : undefined}
      aria-label={buildAriaLabel(player.name, isCreator, isMe)}
    >
      <span
        class="spectator-row__avatar"
        style={`background-color:${player.color}`}
        aria-hidden="true"
      />
      <span class="spectator-row__name">{player.name}</span>
      {isCreator && (
        <span class="spectator-row__badge spectator-row__badge--creator" aria-hidden="true">
          ✏
        </span>
      )}
      {isMe && (
        <span class="spectator-row__badge spectator-row__badge--you" aria-hidden="true">
          you
        </span>
      )}
    </li>
  );
}

function buildAriaLabel(name: string, isCreator: boolean, isMe: boolean): string {
  const parts = [name];
  if (isCreator) parts.push("(creator)");
  if (isMe) parts.push("(you)");
  return parts.join(" ");
}
